import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import {AgGridReact} from "ag-grid-react";
import AgGridUtils from "../../../utils/aggrid-utils.js";
import {useQuery} from "@tanstack/react-query";
import SentryPhoneService from "../../../services/web/sentry-phone-service.js";
import {useCallback, useMemo, useRef, useState} from "react";
import CellRendererButton from "../../../components/ag-grid/cell-renderer-button.jsx";
import {FaGear} from "react-icons/fa6";
import {Modal} from "../../../components/modal/modal.jsx";
import InputCheckbox from "../../../components/input/input-checkbox.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import Button from "../../../components/button/button.jsx";
import {useEntityMutation} from "../../../hooks/use-entity-mutation.js";
import {FaEraser, FaList, FaSearch} from "react-icons/fa";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import useHasPermission from "../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../utils/enums/permission-enum.js";

function FormRow({ children, title }) {
    return (
        <div className="grid grid-cols-[145px_minmax(0,1fr)] gap-4 pl-1 items-center">
            <p className="font-semibold">{title}</p>
            {children}
        </div>
    )
}

const initialFilter = {
    sipUser: "",
    description: "",
}

const initialForm = {
    incomingCall: false,
    outgoingCall: false,
    internalExtension: false,
    description: "",
    openService: false,
    extensionName: "",
    channelId: "",
    priorityId: "",
}

export default function SentryPhoneSettings() {
    const navigate = navigateHook();
    const [open, setOpen] = useState(false);
    const [ramal, setRamal] = useState(null);
    const [filter, setFilter] = useState(initialFilter);
    const [form, setForm] = useState(initialForm);
    const gridRef = useRef(null);
    const gridApi = useRef(null);
    const hasPermission = useHasPermission();
    const canViewCallLogs = hasPermission(PermissionEnum.SENTRY_PHONE_CALL_LOG);

    const onGridReady = useCallback((params) => {
        gridApi.current = params.api;
    }, []);

    const { data: listRamal = [], isLoading } = useQuery({
        queryKey: ["phones-list-ramal"],
        queryFn: () => SentryPhoneService.listRamal(),
    });

    const ramalMutation = useEntityMutation({
        service: SentryPhoneService,
        getInvalidateKeys: () => [["phones-list-ramal"]],
        navigatePath: "sentry-phone-settings",
    });

    const columnDefs = useMemo(() => ([
        { headerName: "Ramal/Usuário", field: "sipUser", maxWidth: 150, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams },
        { headerName: "IP Detectado", field: "ip", width: 150, maxWidth: 170 },
        { headerName: "Descrição", field: "description", flex: 1, minWidth: 240, filter: "agTextColumnFilter", filterParams: AgGridUtils.filterParams, comparator: AgGridUtils.nullLastComparator },
        { headerName: "Servidor SIP", field: "sipServer", width: 180, maxWidth: 220 },
        { headerName: "Histórico", field: "", width: 120, maxWidth: 130, hide: !canViewCallLogs ,
            cellRenderer: ({ data }) => {
            return <CellRendererButton
                        title="Registros de chamadas"
                        icon={FaList}
                        target="secondary"
                        onClick={() => navigate(`/sentry-phone-calls?sipUser=${data.sipUser}`, true)}
                    />
            }
        },
        { headerName: "Ações", field: "", width: 100, maxWidth: 110,
            cellRenderer: ({ data }) => {
            return <CellRendererButton
                        title="Configurações"
                        icon={FaGear}
                        onClick={() => {
                            setRamal(data);

                            setForm({
                                incomingCall: data.incomingCall ?? false,
                                outgoingCall: data.outgoingCall ?? false,
                                internalExtension: data.internalExtension ?? false,
                                description: data.description ?? "",
                                openService: data.openService ?? false,
                                extensionName: data.extensionName ?? "",
                                channelId: data.channelId ?? "",
                                priorityId: data.priorityId ?? "",
                            });

                            setOpen(true);
                        }}
                    />
            }
        },
    ]), [canViewCallLogs]);

    const handleClear = () => {
        setFilter(initialFilter);

        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    };

    const handleSearch = useCallback(() => {
        const api = gridRef.current?.api || gridApi.current;
        if (!api) return;

        const model = {};

        ["sipUser", "description"].forEach((k) => {
            const v = (filter[k] ?? "").trim();
            if (!v) return;

            model[k] = { filterType: "text", type: "contains", filter: v };
        });

        api.setFilterModel(model);
    }, [filter]);

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
        if (e.key === "Escape") handleClear();
    };

    const handleCheckBoxChange = (e) => {
        const { id, checked } = e.target;

        setForm((prev) => ({
            ...prev,
            [id]: checked,
        }));
    }

    const payload = {
        description: form.description,
        incomingCall: form.incomingCall,
        outgoingCall: form.outgoingCall,
        internalExtension: form.internalExtension,
        openService: form.openService,
        extensionName: form.extensionName,
        channelId: form.channelId === "" ? 0 : Number(form.channelId),
        priorityId: form.priorityId === "" ? 0 : Number(form.priorityId),
    }

    const handleUpdate = () => {
        if (!ramal?.sipUser) {
            console.error("sipUser não informado");
            return;
        }

        ramalMutation.mutate({
            method: "update",
            id: ramal.sipUser,
            payload: payload,
        });
    }

    const handleDelete = () => {
        if (!ramal?.sipUser) {
            console.error("sipUser não informado");
            return;
        }

        ramalMutation.mutate({
            method: "delete",
            id: ramal.sipUser,
        });
    }

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>Configurações</Template.Title>
                </Template.Header>
                <Template.Body className="space-y-2">
                    <Card>
                        <Card.Body className="flex gap-4">
                            <InputForm
                                id="sipUser"
                                label="Ramal/Usuário"
                                classNameWrapper="flex-1"
                                value={filter.sipUser}
                                onChange={({target: {id, value}}) => setFilter(prev => ({ ...prev, [id]: value }))}
                                onKeyDown={handleKeyDown}
                            />
                            <InputForm
                                id="description"
                                label="Descrição"
                                classNameWrapper="flex-1"
                                value={filter.description}
                                onChange={({target: {id, value}}) => setFilter(prev => ({ ...prev, [id]: value }))}
                                onKeyDown={handleKeyDown}
                            />
                            <div className="flex gap-2 items-end">
                                <Button
                                    title="Limpar"
                                    target="primary-outline"
                                    onClick={handleClear}
                                >
                                    <FaEraser />
                                </Button>
                                <Button
                                    title="Buscar"
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                    <Card>
                        <Card.Body>
                            <div className="h-[calc(100vh-153px)]">
                                <AgGridReact
                                    onGridReady={onGridReady}
                                    ref={gridRef}
                                    rowData={listRamal}
                                    columnDefs={columnDefs}
                                    defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                    pagination
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    suppressCellFocus={true}
                                    animateRows={false}
                                    gridOptions={{ localeText: AgGridUtils.localization() }}
                                    theme={AgGridUtils.generalTheme()}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>
            <Modal
                open={open}
                setOpen={setOpen}
                title={ramal?.sipUser ?? ""}
                className="w-4/12"
            >
                <Modal.Body className="space-y-4">
                    <Card>
                        <Card.Header>
                            <Card.Title>Configurações</Card.Title>
                        </Card.Header>
                        <Card.Body className="space-y-2">
                            <InputForm
                                id="description"
                                label="Descrição"
                                value={form.description}
                                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                            />
                            <InputCheckbox
                                id="incomingCall"
                                label="Gravar chamadas recebidas"
                                checked={form.incomingCall}
                                onChange={handleCheckBoxChange}
                                required
                                className="pl-1"
                            />
                            <InputCheckbox
                                id="outgoingCall"
                                label="Gravar chamadas realizadas"
                                checked={form.outgoingCall}
                                onChange={handleCheckBoxChange}
                                required
                                className="pl-1"
                            />
                            <InputCheckbox
                                id="internalExtension"
                                label="Gravar chamadas de ramais internos"
                                checked={form.internalExtension}
                                onChange={handleCheckBoxChange}
                                required
                                className="pl-1"
                            />
                        </Card.Body>
                    </Card>
                    <Card>
                        <Card.Header>
                            <Card.Title>Despacho</Card.Title>
                        </Card.Header>
                        <Card.Body className="space-y-2">
                            <InputCheckbox
                                id="openService"
                                label="Abrir atendimento no Despacho"
                                disabled={!form.incomingCall}
                                checked={form.openService}
                                onChange={handleCheckBoxChange}
                                required
                                className="pl-1"
                            />
                            <FormRow title="Identificação do Ramal">
                                <InputForm
                                    id="extensionName"
                                    label=""
                                    value={form.extensionName}
                                    onChange={(e) => setForm((prev) => ({ ...prev, extensionName: e.target.value}))}
                                    disabled={!form.openService}
                                />
                            </FormRow>
                            <FormRow title="ID do canal">
                                <InputForm
                                    id="channelId"
                                    label=""
                                    type="number"
                                    value={form.channelId}
                                    onChange={(e) => setForm((prev) => ({ ...prev, channelId: e.target.value}))}
                                    classNameWrapper="w-30"
                                    disabled={!form.openService}
                                />
                            </FormRow>
                            <FormRow title="ID da prioridade">
                                <InputForm
                                    id="priorityId"
                                    label=""
                                    type="number"
                                    min={0}
                                    value={form.priorityId}
                                    onChange={(e) => setForm((prev) => ({ ...prev, priorityId: e.target.value}))}
                                    classNameWrapper="w-30"
                                    disabled={!form.openService}
                                />
                            </FormRow>
                        </Card.Body>
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button target="danger" onClick={handleDelete} actionFeedbackType="delete">
                        Excluir
                    </Button>
                    <Button onClick={handleUpdate} actionFeedbackType="save">
                        Salvar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}